/**
 * INV-5: Dock Capability Display Formatter
 *
 * Centralized formatting for dock capability badges across operational surfaces.
 * No routing logic — display only.
 */

/**
 * Format dock capabilities into a compact display string.
 *
 * @param dock - Object with temperatureZone and/or handlingCapabilities
 * @returns Formatted string like "Frozen · Clamp", "Mixed-use", or null if no capabilities
 *
 * Rules:
 * - "mixed" / "mixed-use" temperature → renders as "Mixed-use"
 * - handling_capabilities limited to 2 visible items
 * - If >2 handling items, shows "+N" overflow indicator
 * - Display order: temperature first, then handling
 */
export function formatDockCapabilities(
  dock: {
    temperatureZone?: string | null;
    handlingCapabilities?: string | null;
  }
): string | null {
  const parts: string[] = [];

  // Temperature zone
  const tz = (dock.temperatureZone || '').trim();
  if (tz) {
    const tzLower = tz.toLowerCase();
    if (tzLower === 'mixed' || tzLower === 'mixed-use') {
      parts.push('Mixed-use');
    } else {
      // Capitalize first letter
      parts.push(tz.charAt(0).toUpperCase() + tz.slice(1).toLowerCase());
    }
  }

  // Handling capabilities (comma-separated string → max 2 visible + overflow)
  const rawCaps = (dock.handlingCapabilities || '').trim();
  if (rawCaps) {
    const caps = rawCaps
      .split(',')
      .map(c => c.trim())
      .filter(Boolean)
      .map(c => {
        // Normalize: replace underscores/hyphens with spaces, capitalize first word
        const normalized = c.replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
        return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
      });

    if (caps.length > 0) {
      const visible = caps.slice(0, 2);
      const overflow = caps.length - 2;
      parts.push(...visible);
      if (overflow > 0) {
        parts.push(`+${overflow}`);
      }
    }
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}
